import { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import insta360Image from "assets/imgs/insta360.png";
import FunctionalButton from "components/layout/FunctionalButton";
import Insta360WifiSettingsDialog from "components/control/settings/Insta360WifiSettingsDialog";
import { useInsta360Session } from "providers/Insta360SessionProvider";
import { useToast } from "providers/ToastProvider";
import { postInsta360Connect, postInsta360Disconnect } from "lib/r2c2360Api";
import { toUserFacingApiError } from "lib/utils/userFacingApiError";

type Camera360Id = "insta360";

const CAMERA_360_OPTIONS: {
    id: Camera360Id;
    image: string;
    settingsEnabled: boolean;
}[] = [{ id: "insta360", image: insta360Image, settingsEnabled: true }];

const Insta360SettingsPanel: React.FC = () => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const { fsm, baseUrl, ingestState } = useInsta360Session();
    const [busy, setBusy] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [selected, setSelected] = useState<Camera360Id>("insta360");

    const runSession = useCallback(
        async (kind: "connect" | "disconnect") => {
            if (!baseUrl) return;
            setBusy(true);
            try {
                const data =
                    kind === "connect"
                        ? await postInsta360Connect(baseUrl)
                        : await postInsta360Disconnect(baseUrl);
                ingestState(data.state);
            } catch (err) {
                addToast({
                    type: "error",
                    message: toUserFacingApiError(err, t("insta360.failed")),
                });
            } finally {
                setBusy(false);
            }
        },
        [addToast, baseUrl, ingestState, t]
    );

    const connecting = busy || fsm === "associating";
    const sessionActive =
        fsm === "idle" || fsm === "recording" || fsm === "downloading" || fsm === "associating";
    const apiReady = Boolean(baseUrl);
    const canConnect =
        apiReady && !connecting && fsm === "disconnected" && selected === "insta360";
    const canDisconnect =
        apiReady &&
        !connecting &&
        fsm !== "disconnected" &&
        fsm !== "recording" &&
        fsm !== "downloading" &&
        selected === "insta360";

    return (
        <div className="flex flex-col gap-3">
            <div>
                <p className="text-h4 font-semibold text-white">{t("insta360.title")}</p>
                <p className="mt-1 text-h5 text-black40">
                    <Trans
                        i18nKey="insta360.description"
                        ns="settings"
                        components={{
                            1: <span className="text-white" />,
                            2: <span className="text-white" />,
                        }}
                    />
                </p>
                {!apiReady ? (
                    <p className="mt-2 text-h5 text-black60">{t("insta360.connectFirst")}</p>
                ) : null}
            </div>
            <div
                className="grid grid-cols-2 gap-4"
                role="listbox"
                aria-label={t("insta360.listboxAriaLabel")}
            >
                {CAMERA_360_OPTIONS.map((opt) => {
                    const isSel = selected === opt.id;
                    const isActive = sessionActive && opt.id === "insta360";
                    return (
                        <div
                            key={opt.id}
                            role="option"
                            aria-selected={isSel}
                            tabIndex={0}
                            onClick={() => setSelected(opt.id)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setSelected(opt.id);
                                }
                            }}
                            className={`relative flex min-h-0 min-w-0 cursor-pointer flex-row items-center gap-3 overflow-hidden rounded-r10 border bg-black90 p-3 text-left transition-colors ${
                                isActive
                                    ? "border-primary2 ring-2 ring-primary2/40"
                                    : "border-black80 hover:border-black60"
                            }`}
                        >
                            {isActive ? (
                                <span className="absolute right-2 top-2 rounded-r5 border border-primary2 bg-primary2 px-2 py-0.5 text-h5 font-semibold text-white">
                                    {t("payload.active")}
                                </span>
                            ) : null}
                            <div className="relative h-24 w-28 shrink-0 rounded-r5 bg-black100">
                                <img
                                    src={opt.image}
                                    alt=""
                                    className="absolute inset-0 m-auto max-h-[5.5rem] max-w-full object-contain p-1"
                                />
                            </div>
                            <div className="min-w-0 flex-1 py-0.5 pr-2">
                                <p className="text-h5 font-semibold leading-snug text-white">
                                    {t(`insta360.options.${opt.id}.title`)}
                                </p>
                                <p className="mt-1 line-clamp-3 text-h5 leading-snug text-black40">
                                    {t(`insta360.options.${opt.id}.subtitle`)}
                                </p>
                                <button
                                    type="button"
                                    className={`mt-2 rounded-r5 border px-2 py-1 text-h5 font-semibold !normal-case ${
                                        opt.settingsEnabled
                                            ? "border-black60 text-white hover:border-black40"
                                            : "cursor-not-allowed border-black80 text-black60 opacity-50"
                                    }`}
                                    disabled={!opt.settingsEnabled}
                                    aria-disabled={!opt.settingsEnabled}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!opt.settingsEnabled) return;
                                        setSettingsOpen(true);
                                    }}
                                >
                                    {t("payload.settingsButton")}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <FunctionalButton
                    title={busy ? t("common:working") : t("insta360.connect")}
                    className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                    disabled={!canConnect}
                    onClick={() => void runSession("connect")}
                />
                <FunctionalButton
                    title={busy ? t("common:working") : t("common:disconnect")}
                    className="rounded-r5 border border-black60 bg-black90 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:border-black40 disabled:opacity-50"
                    disabled={!canDisconnect}
                    onClick={() => void runSession("disconnect")}
                />
            </div>
            <Insta360WifiSettingsDialog
                visible={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                baseUrl={baseUrl}
            />
        </div>
    );
};

export default Insta360SettingsPanel;
